import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, Shield, Clock } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import SEO from '@/components/SEO';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/api/products`);
        // Get 4 featured products (one from each category)
        const featured = response.data.slice(0, 8);
        setFeaturedProducts(featured);
      } catch (error) {
        // Error handled silently - will show loading state
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);
  
  const features = [
    { id: 'delivery', icon: Truck, title: 'Livraison rapide', titleEn: 'Fast Delivery', desc: 'Partout en Belgique', descEn: 'Throughout Belgium' },
    { id: 'quality', icon: Shield, title: 'Qualité garantie', titleEn: 'Quality Guaranteed', desc: 'Produits frais', descEn: 'Fresh products' },
    { id: 'support', icon: Clock, title: 'Service client', titleEn: 'Customer Service', desc: '7j/7 disponible', descEn: 'Available 7 days' },
  ];
  
  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title="AfriOne B&D"
        description="AfriOne B&D"
        ogTitle="AfriOne B&D"
        ogDescription="AfriOne B&D"
        twitterTitle="AfriOne B&D"
        twitterDescription="AfriOne B&D"
        keywords="épicerie africaine, produits africains, Belgique, livraison, manioc, plantain"
        canonical="https://afrionebd.com/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1640613126891-c2f15e34bb5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwdmVnZXRhYmxlcyUyMHZhcmlldHklMjBwbGFudGFpbiUyMGNhc3NhdmElMjBva3JhJTIwdG9tYXRvZXMlMjBwZXBwZXJzJTIwb25pb25zJTIwZnJlc2glMjBwcm9kdWNlfGVufDB8fHx8MTc3NDUyNTk4N3ww&ixlib=rb-4.1.0&q=85"
            alt="Légumes et Fruits Africains Variés - Marché Frais"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-text_primary/90 via-text_primary/70 to-transparent" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:max-w-2xl"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Découvrez les saveurs authentiques d'Afrique
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8">
              Votre épicerie africaine en ligne en Belgique. Produits frais, qualité garantie.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/products"
                className="px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary_hover transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
                data-testid="hero-shop-button"
              >
                Voir nos produits
              </Link>
              <Link
                to="/about"
                className="px-8 py-4 bg-white/10 backdrop-blur-md text-white border-2 border-white/30 rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300"
              >
                En savoir plus
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="flex items-center gap-4 p-6 bg-white rounded-2xl shadow-sm"
                data-testid={`feature-${feature.id}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-text_primary text-lg mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-text_secondary text-sm">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text_primary mb-4">
              Nos Produits Phares
            </h2>
            <p className="text-lg text-text_secondary max-w-2xl mx-auto">
              Découvrez notre sélection de produits africains authentiques
            </p>
          </motion.div>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={`skeleton-${i}`} className="bg-white rounded-2xl p-4 animate-pulse">
                  <div className="aspect-square bg-gray-200 rounded-xl mb-4" />
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-products-grid">
              {featuredProducts.slice(0, 6).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
          
          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-block px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary_hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              Voir tous les produits →
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary_hover">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Commandez maintenant et recevez chez vous
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Livraison rapide dans toute la Belgique. Produits frais et authentiques.
              </p>
              <Link
                to="/products"
                className="inline-block px-8 py-4 bg-white text-primary rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                Commander maintenant
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
            <img
              src="https://images.pexels.com/photos/4262167/pexels-photo-4262167.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Famille Africaine Réunie"
              className="w-full h-80 lg:h-96 object-cover rounded-3xl shadow-2xl"
            />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Newsletter */}
      <section className="py-16 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-text_primary mb-4">
              Restez informé de nos nouveautés
            </h2>
            <p className="text-lg text-text_secondary mb-8">
              Inscrivez-vous à notre newsletter pour recevoir nos offres exclusives
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <input
                type="email"
                placeholder="Votre email"
                className="px-6 py-4 rounded-full border-2 border-border focus:border-primary outline-none text-lg flex-1 max-w-md"
              />
              <button className="px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary_hover transition-all duration-300 shadow-lg hover:shadow-xl">
                S'inscrire
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}